import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import useSeo from "@/hooks/useSeo";
import { getArticleBySlug, getArticlesByCategory } from "@/mocks/articles";
import { BASE_URL } from "@/config/site";
import { articleSchema, breadcrumbListSchema, faqPageSchema, webPageSchema } from "@/lib/schema";

export default function ArticlePage() {
  const { slug = "" } = useParams();
  const article = useMemo(() => getArticleBySlug(slug), [slug]);
  const [openFaq, setOpenFaq] = useState(0);

  const related = useMemo(
    () => (article ? getArticlesByCategory(article.category).filter((a) => a.slug !== article.slug).slice(0, 4) : []),
    [article]
  );

  useSeo({
    title: article ? article.title : "文章未找到",
    description: article?.summary ?? "该文章暂未找到。",
    keywords: article ? `日漫,${article.categoryName},${article.title}` : "日漫",
    path: `/article/${slug}`,
    jsonLd: article
      ? [
          articleSchema({
            title: article.title,
            description: article.summary,
            path: `/article/${article.slug}`,
            cover: article.cover,
            datePublished: `${article.year}-01-01`,
            categoryName: article.categoryName,
          }),
          breadcrumbListSchema([
            { name: "首页", path: "/" },
            { name: article.categoryName, path: `/category/${article.category}` },
            { name: article.title, path: `/article/${article.slug}` },
          ]),
          webPageSchema(article.title, `/article/${article.slug}`, article.summary),
          faqPageSchema(article.faq),
        ]
      : undefined,
  });

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col">
        <ScrollToTop />
        <Navbar />
        <main className="flex flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">抱歉，未找到这篇推荐</h1>
          <p className="mt-3 text-sm text-foreground-600">内容可能已被更新或删除。</p>
          <Link
            to="/"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <ScrollToTop />
      <Navbar />

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 md:px-6 md:py-12">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: article.categoryName, to: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <div className="mt-6 grid gap-10 lg:grid-cols-[300px_1fr]">
          {/* Sidebar cover + info */}
          <aside>
            <div className="overflow-hidden rounded-lg border border-background-200">
              <img
                src={article.cover}
                alt={`${article.title}封面`}
                title={`${article.title} - 日漫精选推荐`}
                className="aspect-[3/4] w-full object-cover object-top"
              />
            </div>
            <dl className="mt-5 space-y-3 rounded-lg border border-background-200 bg-background-100 p-5 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">评分</dt>
                <dd className="flex items-center gap-1 font-bold text-accent-700">
                  <i className="ri-star-fill" aria-hidden="true" />
                  {article.rating.toFixed(1)}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">题材</dt>
                <dd className="font-medium text-foreground-950">{article.categoryName}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">年份</dt>
                <dd className="font-medium text-foreground-950">{article.year}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">话数</dt>
                <dd className="font-medium text-foreground-950">{article.episodes}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">制作</dt>
                <dd className="text-right font-medium text-foreground-950">{article.studio}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">导演</dt>
                <dd className="text-right font-medium text-foreground-950">{article.director}</dd>
              </div>
            </dl>
            <div className="mt-5 flex flex-wrap gap-2">
              {article.tags.map((t) => (
                <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                  {t}
                </span>
              ))}
            </div>
          </aside>

          {/* Main content */}
          <article>
            <header>
              <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                {article.title}
              </h1>
              <p className="mt-4 border-l-4 border-primary-500 pl-4 text-sm leading-6 text-foreground-600 md:text-base">
                {article.summary}
              </p>
            </header>

            <div className="mt-8 space-y-6">
              {article.content.map((block, i) => (
                <section key={i}>
                  <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                    <span className="flex h-6 w-6 items-center justify-center rounded-md bg-accent-100 text-xs font-black text-accent-800">
                      {i + 1}
                    </span>
                    {block.heading}
                  </h2>
                  <p className="mt-3 text-sm leading-7 text-foreground-700">{block.body}</p>
                </section>
              ))}
            </div>

            {/* FAQ */}
            {article.faq.length > 0 && (
              <section className="mt-10">
                <h2 className="font-heading text-lg font-bold text-foreground-950">常见问题</h2>
                <div className="mt-4 divide-y divide-background-200 rounded-lg border border-background-200">
                  {article.faq.map((item, idx) => (
                    <div key={idx} className="bg-background-50">
                      <button
                        type="button"
                        onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                        aria-expanded={openFaq === idx}
                      >
                        <span className="text-sm font-semibold text-foreground-950">{item.q}</span>
                        <i
                          className={`ri-${openFaq === idx ? "subtract" : "add"}-line text-lg text-primary-700 transition-transform`}
                          aria-hidden="true"
                        />
                      </button>
                      {openFaq === idx && (
                        <p className="px-5 pb-4 text-sm leading-6 text-foreground-600">{item.a}</p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            <div className="mt-8 flex flex-wrap gap-3 border-t border-background-200 pt-6">
              <Link
                to={`/category/${article.category}`}
                className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                查看更多{article.categoryName}
              </Link>
              <Link
                to="/"
                className="whitespace-nowrap rounded-md bg-secondary-100 px-5 py-2.5 text-sm font-semibold text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                返回首页
              </Link>
            </div>
          </article>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-16">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              同类{article.categoryName}神作
            </h2>
            <div className="mt-6 grid grid-cols-2 gap-5 md:grid-cols-4">
              {related.map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>
          </section>
        )}

        {/* canonical share note */}
        <p className="mt-10 text-center text-xs text-foreground-400">
          本文来源：{BASE_URL}/article/{article.slug}
        </p>
      </main>

      <Footer />
    </div>
  );
}